import React from "react";
import { Checkbox, Form, Input, Spin, Typography } from "antd";
import { eventTypeLabel } from "../labels";
import { DEFAULT_API_KEY_HEADER } from "../types";

const HEADER_NAME_PATTERN = /^[A-Za-z0-9!#$%&'*+.^_`|~-]+$/;

const RESERVED_HEADERS = new Set([
  "content-type",
  "content-length",
  "host",
  "user-agent",
  "connection",
  "transfer-encoding",
  "x-webhook-event",
  "x-webhook-event-id",
  "x-webhook-delivery-attempt",
]);

type Props = {
  eventTypes: string[];
  eventTypesLoading?: boolean;
};

export const WebhookSubscriptionFields: React.FC<Props> = ({ eventTypes, eventTypesLoading }) => {
  return (
    <>
      <Form.Item
        label="Namn"
        name="name"
        rules={[
          { required: true, whitespace: true, message: "Ange ett namn" },
          { max: 256, message: "Namnet kan vara högst 256 tecken" },
        ]}
      >
        <Input placeholder="Till exempel lager eller extern butik" maxLength={256} />
      </Form.Item>
      <Form.Item
        label="URL"
        name="url"
        extra="Https-adress som Toolbox kan nå. Lokala och interna adresser nekas."
        rules={[
          { required: true, whitespace: true, message: "Ange en URL" },
          { max: 2048, message: "URL:en kan vara högst 2048 tecken" },
          {
            validator: async (_, value: string) => {
              const url = value?.trim() ?? "";
              if (!url) {
                return;
              }
              let parsed: URL;
              try {
                parsed = new URL(url);
              } catch {
                throw new Error("Ange en fullständig adress, till exempel https://example.com/webhook");
              }
              if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
                throw new Error("Adressen måste använda http eller https");
              }
              if (parsed.username || parsed.password) {
                throw new Error("Adressen får inte innehålla användarnamn eller lösenord");
              }
            },
          },
        ]}
      >
        <Input placeholder="https://example.com/webhooks/verktygsboden" />
      </Form.Item>
      <Form.Item
        label="Header för API-nyckel"
        name="apiKeyHeaderName"
        extra={`Standard är ${DEFAULT_API_KEY_HEADER}. Nyckeln skickas i den här headern på varje anrop.`}
        rules={[
          { max: 128, message: "Headernamnet kan vara högst 128 tecken" },
          {
            validator: async (_, value: string) => {
              const header = value?.trim() ?? "";
              if (!header) {
                return;
              }
              if (!HEADER_NAME_PATTERN.test(header)) {
                throw new Error("Headernamnet måste vara ett giltigt HTTP-headernamn");
              }
              if (RESERVED_HEADERS.has(header.toLowerCase())) {
                throw new Error(`${header} sätts av webhook-avsändaren och kan inte användas för nyckeln`);
              }
            },
          },
        ]}
      >
        <Input placeholder={DEFAULT_API_KEY_HEADER} />
      </Form.Item>
      <Form.Item
        label="Händelser"
        name="eventTypes"
        rules={[{ required: true, type: "array", min: 1, message: "Välj minst en händelse" }]}
      >
        {eventTypesLoading ? (
          <Spin size="small" />
        ) : (
          <Checkbox.Group style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {eventTypes.map((type) => (
              <Checkbox key={type} value={type}>
                {eventTypeLabel(type)}{" "}
                <Typography.Text type="secondary">{type}</Typography.Text>
              </Checkbox>
            ))}
          </Checkbox.Group>
        )}
      </Form.Item>
    </>
  );
};
